import { DisplayUseCase } from "@/usecases/display"
import { AppSelect, AppSelectOption } from "@/widgets/Common"
import type { Resolution } from "@/sdk"
import { Box, Flex, Text, useToast } from "@chakra-ui/react"
import React, { CSSProperties, useEffect, useMemo, useState } from "react"

interface ScaleRatioContentProps {
  resolution: Resolution
  triggerStyle?: CSSProperties
}

const ScaleRatioContent: React.FC<ScaleRatioContentProps> = ({
  resolution,
  triggerStyle,
}) => {
  const [scaleRatios, setScaleRatios] = useState<number[]>([])
  const [recommended, setRecommended] = useState<number | null>(null)
  const [selected, setSelected] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isOperating, setIsOperating] = useState(false)
  const toast = useToast()

  useEffect(() => {
    let isActive = true
    if (resolution.width <= 0 || resolution.height <= 0) {
      setScaleRatios([])
      setRecommended(null)
      setSelected(null)
      setIsLoading(false)
      return () => {
        isActive = false
      }
    }

    setIsLoading(true)
    const loadConfig = async () => {
      const config = await DisplayUseCase.getScaleRatioConfig(resolution)
      if (!isActive) return
      setScaleRatios(config?.scaleRatios ?? [])
      setRecommended(config?.recommended ?? null)
      setSelected(config?.selected ?? null)
      setIsLoading(false)
    }
    loadConfig().catch((error) => {
      console.error("Load scale ratio config error: ", error)
      if (isActive) setIsLoading(false)
    })
    return () => {
      isActive = false
    }
  }, [resolution.height, resolution.width])

  const options = useMemo<AppSelectOption[]>(
    () =>
      scaleRatios.map((scaleRatio) => ({
        label: `${scaleRatio}%${scaleRatio === recommended ? " (推荐)" : ""}`,
        value: scaleRatio,
      })),
    [recommended, scaleRatios]
  )
  const selectedOption = options.find((item) => item.value === selected) ?? {
    label: isLoading ? "加载中" : "暂不可用",
    value: selected,
  }

  const handleChange = async (option: AppSelectOption) => {
    const next = Number(option.value)
    if (isOperating || selected === next) return
    const previous = selected
    setSelected(next)
    setIsOperating(true)
    const isSuccess = await DisplayUseCase.setScaleRatio(next, resolution)
    setIsOperating(false)
    if (!isSuccess) setSelected(previous)
    toast({
      title: isSuccess ? `缩放比已设置为 ${next}%` : "缩放比设置失败",
      status: isSuccess ? "success" : "error",
      duration: 2000,
      isClosable: false,
      position: "top",
      containerStyle: { width: "190px" },
    })
  }

  return (
    <Flex
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      minHeight="34px"
      marginTop="1px"
      paddingTop="3px"
      borderTop="1px solid #ffffff33"
    >
      <Text color="#FFFFFF" fontSize="13px">
        缩放比
      </Text>
      <Box width="185px" flexShrink={0}>
        <AppSelect
          value={selectedOption}
          options={options}
          triggerStyle={triggerStyle}
          onChange={(option) => {
            handleChange(option).catch(console.error)
          }}
        />
      </Box>
    </Flex>
  )
}

export default ScaleRatioContent
